import config from 'config'
import { createSelector } from 'reselect'
import { isPreviouslyOrderedTemplate } from '~/shared/sets/previouslyOrderedTemplate'

import { tileEntityAllIdsSelector, tileEntityByIdSelector } from '~/app/store/tileEntity'
import { getGalleryIdSelector, getGalleryNameSelector, getMpvid, getForcedRankingStrategySelector, getViewId, getProductKey } from '~/app/store/config'
import { filterRefinementsSelector, keywordRefinementSelector } from '~/app/store/refinement/selectors'
import { REFINEMENT_DIMENSION } from 'shared/constants'
import { designByIdSelector } from '~/app/store/design'
import { getEntityImpressionId } from '~/app/store/analytics/entityImpressionId'
import { totalEntityCountSelector } from '~/app/store/paging'
import { getLatsLoadMoreLength } from '~/app/store/config/reducer'
import type { Refinement, IAnalytics, ProductOptionsSet, GalleryImpressions } from 'shared/analytics/interface'
import type { State } from 'shared/types/gallery/state'

export const generateImpressionId = (viewId: string, tileEntityImpressionId: string): string => `${viewId}..${tileEntityImpressionId}`

export const getSelectedFilters = createSelector(
  (state: State.GlobalState) => filterRefinementsSelector(state),
  (state: State.GlobalState) => state.filterCategories,
  (state: State.GlobalState) => state.filterByCategory,
  (state: State.GlobalState) => state.filterOptions,
  (state: State.GlobalState) => state.filterByOption,
  (refinements, filterCategories, filterByCategory, filterOptions, filterByOption) =>
    Object.values(refinements).map((refinement) => {
      if (refinement.dimension === REFINEMENT_DIMENSION.ATTRIBUTE) {
        const filterOption = filterOptions.byId[refinement.value]
        const filter = filterByOption[refinement.value]

        return {
          id: refinement.value,
          title: filterOption?.title,
          parent: filter,
          isDefault: !!refinement.isImplicitDefault,
          type: refinement.dimension,
        } as Refinement
      }

      const category = filterCategories[refinement.value]
      const filter = filterByCategory[refinement.value]

      return {
        id: refinement.value,
        title: category?.title,
        parent: filter,
        isDefault: false,
        type: refinement.dimension,
      } as Refinement
    }),
)

export const galleryImpressionIdSelector = createSelector(
  [
    (state: State.GlobalState, _tileEntityId: string, viewId?: string): string => viewId ?? getViewId(state),
    (_state: State.GlobalState, tileEntityId: string): string => getEntityImpressionId(tileEntityId),
  ],
  generateImpressionId,
)

export const galleryImpressionsTrackingSelectorFactory = () =>
  createSelector(
    [
      tileEntityAllIdsSelector,
      tileEntityByIdSelector,
      designByIdSelector,
      getGalleryNameSelector,
      getGalleryIdSelector,
      getMpvid,
      getProductKey,
      getSelectedFilters,
      getForcedRankingStrategySelector,
      keywordRefinementSelector,
      getViewId,
      totalEntityCountSelector,
      getLatsLoadMoreLength,
      (_state: State.GlobalState, analytics: IAnalytics): number => analytics.getIncrementedSessionStep(),
      (_state: State.GlobalState, _analytics: IAnalytics, location: string): string => location,
    ],
    (
      tileEntityIds,
      tileEntityById,
      designById,
      galleryName,
      internalGalleryId,
      mpvId,
      galleryProductKey,
      refinements,
      forcedRankingStrategy,
      keywordRefinement,
      viewId,
      totalEntityCount,
      latsLoadMoreLength,
      sessionStep,
      location,
    ) => {
      const productOptionsMap = new Map<string, ProductOptionsSet>()
      const adjustedTileEntityIds = tileEntityIds.slice(-latsLoadMoreLength)

      return {
        galleryId: galleryName,
        galleryName,
        internalGalleryId,
        productKeys: [galleryProductKey],
        sessionStep,
        refinements,
        forcedRankingStrategy,
        totalEntityCount,
        entities: adjustedTileEntityIds.map((tileEntityId) => {
          const tileEntity = tileEntityById(tileEntityId)

          const {
            fullProductOptions,
            productKey,
            product,
            colorSwatches,
            isForceRanked,
            designCreationType,
            position,
            designConceptId,
            externalId,
            targetUseCase,
            boostInfo,
            rankingSet,
            designTypeMetadata,
          } = tileEntity
          const colorSwatchValues = colorSwatches.map((cs) => designById(cs).color?.split('#')[1]).filter(Boolean)

          let optionsSetId = productOptionsMap.get(tileEntity.productOptionsHash)?.id

          if (!optionsSetId) {
            // start IDs at 1
            optionsSetId = productOptionsMap.size + 1

            productOptionsMap.set(tileEntity.productOptionsHash, {
              id: optionsSetId,
              options: fullProductOptions,
            })
          }

          const { conceptKitId, useCaseId, layoutId } = designTypeMetadata || {}

          return {
            mpvId,
            productKey,
            product,
            galleryImpressionId: generateImpressionId(viewId, getEntityImpressionId(tileEntityId)),
            colorSwatchValues,
            designId: tileEntityId,
            position,
            isForceRanked,
            productOptionsSetId: optionsSetId,
            designCreationType,
            designConceptId,
            externalId,
            targetUseCase,
            isBoosted: boostInfo?.boosted || false,
            rankingSet,
            isPreviouslyOrdered: isPreviouslyOrderedTemplate(rankingSet),
            conceptKitId,
            useCaseId,
            layoutId,
          }
        }),
        route: config.client.segmentRoute,
        location,
        isAutosuggestSearch: !!keywordRefinement?.isAutosuggest,
        productOptionsSets: Array.from(productOptionsMap.values()),
      } as GalleryImpressions
    },
  )

export const galleryImpressionsTrackingSelector = galleryImpressionsTrackingSelectorFactory()
